import DOMPurify from 'isomorphic-dompurify'

/**
 * Formats menu item description for display with line breaks and HTML.
 * Use with v-html to render newlines as <br> and allow safe HTML from the backend.
 *
 * Descriptions are authored in the Tiptap editor (RichTextEditor.vue) and stored
 * by the API as an unvalidated string, so what comes back is attacker-controllable
 * markup rendered with v-html on the public storefront. `format` sanitizes against
 * an allowlist of exactly what the editor can produce: legitimate formatting is
 * untouched, scripts and event handlers are dropped.
 */

// Tiptap StarterKit's node and mark set, plus the Link and Underline extensions
// RichTextEditor.vue loads. The editor cannot emit anything outside this list.
const ALLOWED_TAGS = [
  'p',
  'br',
  'span',
  'strong',
  'b',
  'em',
  'i',
  'u',
  's',
  'del',
  'code',
  'pre',
  'blockquote',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'ul',
  'ol',
  'li',
  'hr',
  'a'
]

const ALLOWED_ATTR = ['href', 'target', 'rel', 'class']

// A link opened in a new tab hands window.opener to the destination unless it is
// disclaimed. Tiptap sets this itself; enforcing it here also covers rows written
// by anything else that reached the column.
DOMPurify.addHook('afterSanitizeAttributes', (node) => {
  const target = node.getAttribute('target')
  if (node.tagName === 'A' && target !== null && target.length > 0) {
    node.setAttribute('rel', 'noopener noreferrer')
  }
})

export function useMenuDescriptionFormat() {
  function format(description: string | null | undefined): string {
    if (description == null || description === '') return ''
    const clean = DOMPurify.sanitize(description, {
      ALLOWED_TAGS,
      ALLOWED_ATTR,
      ALLOW_DATA_ATTR: false
    })
    return clean.replace(/\n/g, '<br>')
  }

  function toText(description: string | null | undefined): string {
    return format(description)
      .replace(/<br\s*\/?>/gi, ' ')
      .replace(/<\/(p|div|li|ul|ol)>/gi, ' ')
      .replace(/<[^>]*>/g, '')
      .replace(/&nbsp;/g, ' ')
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'")
      .replace(/\s+/g, ' ')
      .trim()
  }

  return { format, toText }
}
